import Service from '@ember/service';
import Html2ToCanvas from 'html2canvas';
import type LoggingService from './logger';
import { service } from '@ember/service';

const generateStyleDefs = function (svgDomElement: Element, logger: LoggingService): void {
  // method for adding styles inline for the svg to png conversion, in progress
  let styleDefs = '';
  const sheets = document.styleSheets;
  for (const sheet of sheets) {
    try {
      const csshref = sheet.href;
      // console.log(csshref, ':', sheet.cssRules.length > 1 && (sheet.cssRules[1] as CSSStyleRule).selectorText : '');
      if (
        (csshref && csshref.includes(window.location.origin)) ||
        // The following line is a dreadfully fragile hack to use the ea-graph CSS and exclude all the anonymous CSS modules introduced by analytics
        (!csshref && sheet.cssRules.length > 1 && (sheet.cssRules[1] as CSSStyleRule).selectorText === '.titletext')
      ) {
        const rules = sheet.cssRules;
        for (let j = 0; j < rules.length; j++) {
          const rule = rules[j] as CSSStyleRule;
          if (rule.style) {
            const selectorText = rule.selectorText;
            try {
              const elems = selectorText.indexOf('::') === -1 ? svgDomElement.querySelectorAll(selectorText) : [];
              if (elems.length || selectorText === ':root') {
                styleDefs += selectorText + ' { ' + rule.style.cssText + ' }\n';
              }
            } catch (error: unknown) {
              // We should never get here but this will keep failures within one rule from stopping processing
              const namedError = error as { message: string };
              logger.log(`Error on rule #${j} in ${csshref} with "${selectorText}":` + namedError.message.toString());
            }
          }
        }
      }
    } catch (error: unknown) {
      const namedError = error as { message: string };
      logger.log('Error:' + namedError.message.toString());
    }
  }
  const s = document.createElement('style');
  s.setAttribute('type', 'text/css');
  s.innerHTML = styleDefs;
  const defs = document.createElement('defs');
  defs.appendChild(s);
  svgDomElement.insertBefore(s, svgDomElement.firstChild);
};

export default class SnapShotService extends Service {
  @service declare logger: LoggingService;

  convertSVGToImage(componentId: string): Promise<PDFImageInfo> {
    let imageData;
    const canvas = document.createElement('canvas');
    return new Promise((resolve, reject) => {
      const svgElem: Element | null = document.querySelector(componentId + ' svg');
      if (svgElem) {
        generateStyleDefs(svgElem, this.logger);
        canvas.width = (svgElem as SVGSVGElement).width.baseVal.value;
        canvas.height = (svgElem as SVGSVGElement).height.baseVal.value;
      }
      const svgString = new XMLSerializer().serializeToString(svgElem as Node);
      const ctx = canvas.getContext('2d');
      const DOMURL = window.URL || window.webkitURL;
      const img = new Image();
      const svg = new Blob([svgString], {
        type: 'image/svg+xml;charset=utf-8',
      });
      const url = DOMURL.createObjectURL(svg);
      img.onload = () => {
        if (ctx) ctx.drawImage(img, 0, 0);
        imageData = canvas.toDataURL('image/png');
        DOMURL.revokeObjectURL(url);
        if (imageData !== null) {
          resolve({
            data: imageData,
            width: img.width,
            height: img.height,
            format: 'PNG',
          });
        } else {
          reject({ data: null, width: 0, height: 0, format: 'PNG' });
        }
      };
      img.src = url;
    });
  }

  convertHTMLToImage(componentId: string): Promise<PDFImageInfo> {
    let imageData;
    return new Promise((resolve, reject) => {
      const svgItemList = document.querySelectorAll(componentId + ' svg');
      for (const svgItem of svgItemList) {
        // If there are SVG Items into your HTML, try to get the styles into the SVG
        generateStyleDefs(svgItem, this.logger);
      }
      // Convert HTML into PNG Image
      const htmlItem: HTMLElement | null = document.querySelector(componentId);
      if (htmlItem) {
        Html2ToCanvas(htmlItem, { scrollX: 0, scrollY: 0 }).then((canvas: HTMLCanvasElement) => {
          imageData = canvas.toDataURL('image/png');
          if (imageData !== null) {
            resolve({
              data: imageData,
              width: canvas.width,
              height: canvas.height,
              format: 'PNG',
            });
          } else {
            reject({ data: null, width: 0, height: 0, format: 'PNG' });
          }
        });
      } else {
        reject({ data: null, width: 0, height: 0, format: 'PNG' });
      }
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    snapshot: SnapShotService;
  }
}
